import React, { useEffect, useState, useRef } from "react";
import Search from "./search";
import Button from "../common/Button";
import List from "./list/index.jsx";
import { useDispatch, useSelector } from "react-redux";
import { getPatients, setPatientsQueryParams } from "../../redux/modules/patient/actions";
import { useTruePagination } from "../../helpers/true-pagination";
import Create from "./Create";
import DuplicatePatientDialog from "./create/DuplicatePatientDialog";
import { useNavigate } from "react-router-dom";
import ListLoading from "./list/loading";
import CustomInput from "../common/CustomInput";
import {
  advanceSearchCheckBoxOptions,
  patientInactiveStatusOptions,
} from "./create/options";
import { extractPhoneNumber } from "../../helpers/functions.js";
import {
  purgeClinicalServices,
  updateMedicalReviewStatus,
  updateMinorAilmentStatus,
} from "../../redux/modules/clinical-services/actions.js";
import CommonSelect from "./clinical-services/services/select/common-creatable-select.jsx";

// Guard against React StrictMode double-mount in development.
// Ensures purge runs once per app lifecycle.
let hasPurgedClinicalServicesOnce = false;
const logPatientsDebug = () => {};

const PatientList = ({ create = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showCreateSlider, setShowCreateSlider] = useState(create);
  const {
    patients,
    loading,
    queryParams = {},
  } = useSelector((state) => state.patients);
  const {
    page = 1,
    size = 8,
    sort = "",
    order = "",
    search = "",
    advancedDob = "",
    advancedPhone = "",
    advancedHealthCard = "",
    advancedVersionCode = "",
  } = queryParams;
  const [advanceSearchCheckBox, setAdvanceSearchCheckBox] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [phone, setPhone] = useState("");
  const [healthCardNumber, setHealthCardNumber] = useState("");
  const [healthCardVersionCode, setHealthCardVersionCode] = useState("");
  const [status, setStatus] = useState({
    label: patientInactiveStatusOptions.find((s) => s.value === "active")
      ?.label,
    value: "active",
  });
  const [advancedStatus, setAdvancedStatus] = useState("");
  const [clickedAdvancedSearch, setClickedAdvancedSearch] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicatePatientName, setDuplicatePatientName] = useState("");
  const [duplicatePatientDob, setDuplicatePatientDob] = useState(null);
  const [duplicatePatientId, setDuplicatePatientId] = useState("");
  const hasAnySearchQuery =
    String(search || "").trim().length > 0 ||
    String(advancedDob || "").trim().length > 0 ||
    String(advancedPhone || "").trim().length > 0 ||
    String(advancedHealthCard || "").trim().length > 0 ||
    String(advancedVersionCode || "").trim().length > 0;
  const disableSearchInput = !loading && !hasAnySearchQuery && Array.isArray(patients?.content) && patients.content.length <= 1;

  const hideDialogTimeoutRef = useRef(null);
  const focusedAdvancedRef = useRef(false);
  const lastPatientsQueryRef = useRef("");
  const MIN_SEARCH_LEN = 3;

  // Helpers for duplicate detection
  const normalize = (v) =>
    String(v || "")
      .replace(/-/g, "")
      .trim()
      .toLowerCase();
  const normalizeDob = (d) => String(d || "").split("T")[0];
  const normalizePhone = (v) => extractPhoneNumber(v || "");

  const groupBy = (arr, keyFn) => {
    const m = {};
    (arr || []).forEach((it) => {
      const k = keyFn(it);
      if (!k) return;
      (m[k] = m[k] || []).push(it);
    });
    return m;
  };

  const findDupGroup = (groups) =>
    Object.values(groups || {}).find((g) => g.length > 1);

  const isAllFiltersEmpty = ({
    freeText,
    localDob,
    localPhone,
    localHealthCard,
    localVersion,
    reduxDob,
    reduxPhone,
    reduxHealthCard,
  }) => {
    return (
      !String(freeText || "").trim() &&
      !String(localDob || "").trim() &&
      !String(localPhone || "").trim() &&
      !String(localHealthCard || "").trim() &&
      !String(localVersion || "").trim() &&
      !String(reduxDob || "").trim() &&
      !String(reduxPhone || "").trim() &&
      !String(reduxHealthCard || "").trim()
    );
  };

  const resetLocalAdvancedFilters = () => {
    setDateOfBirth("");
    setPhone("");
    setHealthCardNumber("");
    setHealthCardVersionCode("");
    setAdvancedStatus("");
    focusedAdvancedRef.current = false;
  };

  const clearSearchState = () => {
    resetLocalAdvancedFilters();
    setShowDuplicateDialog(false);

    dispatch(
      setPatientsQueryParams({
        page: 1,
        search: "",
        advancedDob: "",
        advancedPhone: "",
        advancedHealthCard: "",
        advancedVersionCode: "",
      }),
    );
  };

  const findAnyDuplicateGroup = (list) => {
    const lastNameGroups = groupBy(list, (p) => normalize(p.lastName));
    const dobGroups = groupBy(list, (p) => normalizeDob(p.dateOfBirth));
    const hcGroups = groupBy(list, (p) =>
      normalize(p.provincialInsurance?.cardNumber),
    );
    const phoneGroups = groupBy(list, (p) =>
      normalizePhone(
        p.mobileNumber ||
          p.phone ||
          p.contactNumber ||
          p?.contactInfo?.primaryPhone,
      ),
    );

    return (
      findDupGroup(lastNameGroups) ||
      findDupGroup(dobGroups) ||
      findDupGroup(hcGroups) ||
      findDupGroup(phoneGroups)
    );
  };

  // Determine duplicate group based on current search mode/fields.
  // Keeps behavior identical to the previous inline implementation but
  // centralizes the logic for readability and testing.
  const getDuplicateGroup = (
    list,
    { advanceMode, dob, hcNumber, phoneQ, freeText },
  ) => {
    let dupGroup = null;

    if (advanceMode && (dob || hcNumber || phoneQ)) {
      // Prefer phone and health-card matching when those queries are present
      // so that digit-based searches trigger the correct duplicate detection
      // even if an unrelated `dob` value is present in the params.
      if (phoneQ) {
        const target = normalizePhone(phoneQ);
        const matches = list.filter((p) => {
          const pPhone = normalizePhone(
            p.mobileNumber || p.phone || p.contactNumber || p?.contactInfo?.primaryPhone,
          );
          return (pPhone && target && (pPhone.includes(target) || target.includes(pPhone)));
        });
        if (matches.length > 1) dupGroup = matches;
      } else if (hcNumber) {
        const target = normalize(hcNumber);
        const matches = list.filter((p) => {
          const pHc = normalize(p.provincialInsurance?.cardNumber);
          return (pHc && target && (pHc.includes(target) || target.includes(pHc)));
        });
        if (matches.length > 1) dupGroup = matches;
      } else if (dob) {
        const target = normalizeDob(dob).replace(/-/g, "");
        const matches = list.filter(
          (p) => normalizeDob(p.dateOfBirth).replace(/-/g, "") === target,
        );
        if (matches.length > 1) dupGroup = matches;
      }
    } else if (
      !advanceMode &&
      freeText &&
      String(freeText).trim().length >= MIN_SEARCH_LEN
    ) {
      const q = String(freeText || "").trim();
      const digits = q.replace(/\D/g, "");
      if (digits.length > 0) {
        const matches = list.filter((p) => {
          const pdob = normalizeDob(p.dateOfBirth).replace(/-/g, "");
          const ph = normalizePhone(
            p.mobileNumber ||
              p.phone ||
              p.contactNumber ||
              p?.contactInfo?.primaryPhone,
          );
          const hc = normalize(p.provincialInsurance?.cardNumber);
          return pdob === digits || ph === digits || hc === digits;
        });
        if (matches.length > 1) dupGroup = matches;
      } else {
        dupGroup = findAnyDuplicateGroup(list);
      }
    } else {
      dupGroup = findAnyDuplicateGroup(list);
    }

    return dupGroup;
  };

  useEffect(() => {
    const statusFilter = queryParams?.status || "active";
    const normalized = {
      page,
      size,
      sort,
      order,
      search,
      advancedDob,
      advancedPhone,
      advancedHealthCard,
      advancedVersionCode,
      status: statusFilter,
    };

    const queryString = Object.keys(normalized)
      .filter((key) => normalized[key] !== undefined && normalized[key] !== null && normalized[key] !== "")
      .map((key) => `${key}=${normalized[key]}`)
      .join("&");

    if (queryString === lastPatientsQueryRef.current) {
      logPatientsDebug("PatientList effect:skip duplicate query", { queryString });
      return;
    }
    lastPatientsQueryRef.current = queryString;

    logPatientsDebug("PatientList effect:getPatients dispatch", { queryString, normalized });
    dispatch(getPatients(normalized));
  }, [
    dispatch,
    page,
    size,
    sort,
    order,
    search,
    advancedDob,
    advancedPhone,
    advancedHealthCard,
    advancedVersionCode,
    queryParams?.status,
  ]);

  useEffect(() => {
    if (hideDialogTimeoutRef.current) {
      clearTimeout(hideDialogTimeoutRef.current);
      hideDialogTimeoutRef.current = null;
    }

    if (loading) return;

    if (!patients || !patients.content) {
      if (!search || !search.trim()) {
        setShowDuplicateDialog(false);
      }
      return;
    }

    const allFiltersEmpty = isAllFiltersEmpty({
      freeText: search,
      localDob: dateOfBirth,
      localPhone: phone,
      localHealthCard: healthCardNumber,
      localVersion: healthCardVersionCode,
      reduxDob: advancedDob,
      reduxPhone: advancedPhone,
      reduxHealthCard: advancedHealthCard,
    });

    // Close duplicate dialog immediately when all filters are cleared.
    if (allFiltersEmpty) {
      setShowDuplicateDialog(false);
      return;
    }

    if (advanceSearchCheckBox && !dateOfBirth && !healthCardNumber && !phone) {
      setShowDuplicateDialog(false);
      return;
    }

    // While any advanced input is focused, don't show the duplicate dialog
    // unless the user explicitly clicked the advanced "Search" button
    // (toggled via `clickedAdvancedSearch`) to perform a search.
    if (focusedAdvancedRef.current && !clickedAdvancedSearch) {
      setShowDuplicateDialog(false);
      return;
    }

    const list = (patients.content || []).filter(Boolean);
    if (list.length <= 1) {
      // delay hiding a bit to avoid flicker when results are updating
      hideDialogTimeoutRef.current = setTimeout(
        () => setShowDuplicateDialog(false),
        250,
      );
      return;
    }

    const effectiveAdvanceMode =
      advanceSearchCheckBox ||
      !!advancedDob ||
      !!advancedPhone ||
      !!advancedHealthCard;
    const dupGroup = getDuplicateGroup(list, {
      advanceMode: effectiveAdvanceMode,
      dob: advanceSearchCheckBox ? dateOfBirth : advancedDob || dateOfBirth,
      hcNumber: advanceSearchCheckBox
        ? healthCardNumber
        : advancedHealthCard || healthCardNumber,
      phoneQ: advanceSearchCheckBox ? phone : advancedPhone || phone,
      freeText: search,
    });
    if (dupGroup) {
      const first = dupGroup[0];
      setDuplicatePatientName(
        `${first.lastName || ""}${first.firstName ? `, ${first.firstName}` : ""}`,
      );
      setDuplicatePatientDob(first.dateOfBirth || null);
      setDuplicatePatientId(first.id || first.patientId || "");
      setShowDuplicateDialog(true);
    } else {
      // delay hiding to prevent a quick hide/show while typing
      hideDialogTimeoutRef.current = setTimeout(
        () => setShowDuplicateDialog(false),
        250,
      );
    }
  }, [
    patients,
    search,
    dateOfBirth,
    healthCardNumber,
    healthCardVersionCode,
    phone,
    advancedDob,
    advancedPhone,
    advancedHealthCard,
    advanceSearchCheckBox,
    clickedAdvancedSearch,
    loading,
  ]);

  useEffect(() => {
    return () => {
      if (hideDialogTimeoutRef.current) {
        clearTimeout(hideDialogTimeoutRef.current);
        hideDialogTimeoutRef.current = null;
      }
    };
  }, []);

  const paginationRange = useTruePagination({
    totalPageCount: patients.totalPages,
    siblingCount: 1,
    currentPage: page,
  });

  useEffect(() => {
    if (!advanceSearchCheckBox) {
      setDateOfBirth("");
      setPhone("");
      setHealthCardNumber("");
      setHealthCardVersionCode("");
      setAdvancedStatus("");
      dispatch(
        setPatientsQueryParams({
          page: 1,
          advancedDob: "",
          advancedPhone: "",
          advancedHealthCard: "",
          advancedVersionCode: "",
          status: status?.value === "all" ? "" : status?.value || "active",
        }),
      );
    }
  }, [advanceSearchCheckBox]);

  useEffect(() => {
    if (hasPurgedClinicalServicesOnce) {
      return;
    }

    hasPurgedClinicalServicesOnce = true;
    dispatch(purgeClinicalServices());
  }, [dispatch]);

  const manageClick = () => {
    document.body.classList.toggle("overflow-hidden");
  };

  const isFilterApplied = () => {
    const hasSearch = String(search || "").trim().length > 0;
    const hasDob = String(dateOfBirth || "").trim().length > 0;
    const hasPhone = String(phone || "").trim().length > 0;
    const hasHc = String(healthCardNumber || "").trim().length > 0;
    const hasVersion = String(healthCardVersionCode || "").trim().length > 0;
    return (
      advanceSearchCheckBox || hasSearch || hasDob || hasPhone || hasHc || hasVersion || Boolean(status)
    );
  };

  const handleClearAdvancedSearch = () => {
    resetLocalAdvancedFilters();
    dispatch(
      setPatientsQueryParams({
        page: 1,
        search: "",
        advancedDob: "",
        advancedPhone: "",
        advancedHealthCard: "",
        advancedVersionCode: "",
      }),
    );
  };

  const handleStatusChange = (e) => {
    setStatus(e);
    dispatch(
      setPatientsQueryParams({
        page: 1,
        status: e.value === "all" ? "" : e.value,
      }),
    );
  };

  const fetchAdvancedPatients = () => {
    if (!advanceSearchCheckBox) return;

    const statusToUse = advancedStatus || status?.value || "active";
    dispatch(
      setPatientsQueryParams({
        page: 1,
        search: "",
        advancedDob: dateOfBirth,
        advancedPhone: extractPhoneNumber(phone),
        advancedHealthCard: extractPhoneNumber(healthCardNumber),
        advancedVersionCode: healthCardVersionCode,
        status: statusToUse === "all" ? "" : statusToUse,
      }),
    );
  };

  // Clear helpers that also reset focus flag and re-fetch so clearing happens with one click
  const clearPhoneField = () => {
    setPhone("");
    focusedAdvancedRef.current = false;
  };

  const clearHealthCardField = () => {
    setHealthCardNumber("");
    focusedAdvancedRef.current = false;
  };

  const clearVersionCodeField = () => {
    setHealthCardVersionCode("");
    focusedAdvancedRef.current = false;
  };

  const handleSortOrder = (newSort, newOrder) => {
    dispatch(
      setPatientsQueryParams({ sort: newSort, order: newOrder, page: 1 }),
    );
  };

  // useEffect(() => {
  //   medicationReviews?.forEach((value) => {
  //     dispatch(updateMedicalReviewStatus(value.id, "deleted"));
  //   });
  //   minorAilments?.forEach((value) => {
  //     dispatch(updateMinorAilmentStatus(value.id, "deleted"));
  //   });
  // }, []);

  return (
    <div
      className="flex flex-col flex-1 px-5 py-5 bg-right-bottom bg-no-repeat bg-contain "
      style={{ backgroundImage: `url(/images/bg-dots.svg)` }}
    >
      <h1 className="font-semibold text-teal-600 text-body-xl">
        Search Patient
      </h1>
      <div className="w-full mt-3 flex items-center justify-between gap-4 lg:grid lg:grid-cols-6">
        <div className="w-full lg:col-span-3">
          <Search
            disabled={advanceSearchCheckBox || disableSearchInput}
            className=""
            onSearchCleared={clearSearchState}
          />
        </div>
        <div>
          <CommonSelect
            onChange={(e) => handleStatusChange(e)}
            isCreatable={false}
            options={patientInactiveStatusOptions}
            className="!border-gray-400"
            label="Status"
            value={status}
            removeOptional={true}
            disabled={advanceSearchCheckBox}
          />
        </div>
        <Button
          text={
            sort === "lastViewed" ? "View All Patients" : "View Recent Patients"
          }
          onClick={() => {
            handleSortOrder(sort === "lastViewed" ? "" : "lastViewed", "desc");
          }}
          className="h-[70px] w-[19.3%] lg:w-full lg:col-span-1"
          type={"primary"}
        />
        <Button
          text="Add Patient"
          iconUrl="/icons/add.svg"
          onClick={() => {
            setShowCreateSlider(true);
            manageClick();
          }}
          iconBefore
          className="h-[70px] w-[19.3%] lg:w-full lg:col-span-1"
        />
      </div>
      <div className="mt-4">
        <div className="flex flex-row gap-8">
          <CustomInput
            options={advanceSearchCheckBoxOptions}
            type="checkbox"
            name="advanceSearchCheckBox"
            onChange={(e) => {
              setAdvanceSearchCheckBox(e.target.checked);
            }}
            value={advanceSearchCheckBox}
          />
          {!!(dateOfBirth || phone || healthCardNumber) && (
            <div
              className="text-primary cursor-pointer text-gray-800 underline"
              onClick={handleClearAdvancedSearch}
            >
              Clear all
            </div>
          )}
        </div>
        {advanceSearchCheckBox && (
          <div className="grid grid-cols-1 gap-4 mt-3 lg:grid-cols-6 md:grid-cols-2">
            <CustomInput
              name="dateOfBirth"
              className="bg-white border-gray-400"
              label="Date of Birth"
              type="date"
              value={dateOfBirth}
              onChange={(value) => setDateOfBirth(value || null)}
              onFocus={() => {
                focusedAdvancedRef.current = true;
                if (hideDialogTimeoutRef.current) {
                  clearTimeout(hideDialogTimeoutRef.current);
                  hideDialogTimeoutRef.current = null;
                }
                setShowDuplicateDialog(false);
              }}
              onBlur={() => {
                focusedAdvancedRef.current = false;
              }}
              showOptionalLabel={false}
              clearable={!!dateOfBirth}
              onClear={() => setDateOfBirth(null)}
            />
            <CustomInput
              name="phone"
              className="bg-white border-gray-400"
              label="Phone"
              mask="999-999-9999"
              placeholder="XXX XXX XXXX"
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onFocus={() => {
                focusedAdvancedRef.current = true;
                if (hideDialogTimeoutRef.current) {
                  clearTimeout(hideDialogTimeoutRef.current);
                  hideDialogTimeoutRef.current = null;
                }
                setShowDuplicateDialog(false);
              }}
              onBlur={() => {
                focusedAdvancedRef.current = false;
              }}
              showOptionalLabel={false}
              clearable={!!phone}
              showInputCancellation={!!phone}
              onClose={clearPhoneField}
              onClear={clearPhoneField}
            />
            <CustomInput
              name="healthCard"
              className="bg-white border-gray-400"
              label="Health Card"
              mask="9999-999-999"
              placeholder="XXXX XXX XXX"
              type="text"
              value={healthCardNumber}
              onChange={(e) => setHealthCardNumber(e.target.value)}
              onFocus={() => {
                focusedAdvancedRef.current = true;
                if (hideDialogTimeoutRef.current) {
                  clearTimeout(hideDialogTimeoutRef.current);
                  hideDialogTimeoutRef.current = null;
                }
                setShowDuplicateDialog(false);
              }}
              onBlur={() => {
                focusedAdvancedRef.current = false;
              }}
              showOptionalLabel={false}
              clearable={!!healthCardNumber}
              showInputCancellation={!!healthCardNumber}
              onClose={clearHealthCardField}
              onClear={clearHealthCardField}
            />
            <CustomInput
              name="versionCode"
              className="bg-white border-gray-400"
              label="Version Code"
              placeholder="ER"
              type="versionCode"
              value={healthCardVersionCode}
              onChange={(e) => {
                setHealthCardVersionCode(e.target.value.toUpperCase());
              }}
              onFocus={() => {
                focusedAdvancedRef.current = true;
                if (hideDialogTimeoutRef.current) {
                  clearTimeout(hideDialogTimeoutRef.current);
                  hideDialogTimeoutRef.current = null;
                }
                setShowDuplicateDialog(false);
              }}
              onBlur={() => {
                focusedAdvancedRef.current = false;
              }}
              showOptionalLabel={false}
              clearable={!!healthCardVersionCode}
              showInputCancellation={!!healthCardVersionCode}
              onClose={clearVersionCodeField}
              onClear={clearVersionCodeField}
            />
            <CustomInput
              options={patientInactiveStatusOptions}
              name={`advancedStatus`}
              className="bg-white border-gray-400"
              label="Status"
              type="dropdown"
              value={advancedStatus}
              onChange={(e) => setAdvancedStatus(e.value)}
              showOptionalLabel={false}
            />
            <Button
              text="Search"
              onClick={() => {
                setClickedAdvancedSearch(!clickedAdvancedSearch);
                fetchAdvancedPatients();
              }}
            />
          </div>
        )}
      </div>
      <div className="mt-6">
        <List
          patientList={patients.content}
          totalPages={patients.totalPages}
          paginationRange={paginationRange}
          activePage={page}
          loading={loading}
          // Passing this to mitigate flickness while searching when we have no data pop-up
          isFilterApplied={isFilterApplied()}
          search={search}
          advanceSearchCheckBox={advanceSearchCheckBox}
        />
      </div>

      <Create
        open={showCreateSlider}
        onClose={(value) => {
          setShowCreateSlider(value);
          manageClick();
        }}
      />
      <DuplicatePatientDialog
        open={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
        onCancel={() => setShowDuplicateDialog(false)}
        onGoToPatientChart={() => {
          setShowDuplicateDialog(false);
          if (duplicatePatientId) navigate(`/patient/${duplicatePatientId}`);
        }}
        patientName={duplicatePatientName}
        dateOfBirth={duplicatePatientDob}
      />
    </div>
  );
};

export default PatientList;
